<template>
  <div>
    <!-- white header; hidden when page top is visible -->
    <div id="whiteBar" :hidden="!showWhiteHeader">
      <Header />
    </div>

    <div class="intro">
      <!-- transparent header; retain height to avoid content shifting -->
      <div :style="{ 'height': headerHeight + 'px' }">
        <header class="transparent-bar" :hidden="showWhiteHeader">
          <Navigation />
        </header>
      </div>
      <div class="container">
        <img class="merry-go-round" :class="{ 'round-and-round': roundAndRound }"
             src="../assets/img/logo/logo-white-130.png" alt="" aria-hidden="true" />
        <h1 aria-label="Portfolio Homepage">Debye Leong</h1>
        <p class="role">Front-end Developer & UX Engineer</p>
        <p>
          A web developer since 2002 focusing on front-end development and user experience.<br />
          Knowledgeable in Section 508 with a graduate degree in Human-Computer Interaction.
        </p>
        <a href="#featured" class="box">Featured Projects Overview</a>
      </div>
    </div>
    <main>
      <section class="skillset">
        <h1>{{ summary.skillset.name }}</h1>
        <div class="segments">
          <div v-for="(group, i) in summary.skillset.groups" :key="`grp${i}`">
            <template v-if="group.type === 'implementation'">
              <img :src="`/icons/${projectTypeIcon.IMPLEMENTATION}-light.svg`" alt="">
            </template>
            <template v-else-if="group.type === 'research'">
              <img :src="`/icons/${projectTypeIcon.RESEARCH}-light.svg`" alt="">
            </template>
            <template v-else-if="group.type === 'design'">
              <img :src="`/icons/${projectTypeIcon.DESIGN}-light.svg`" alt="">
            </template>
            <h2>{{ group.type }}</h2>
            <ul>
              <li v-for="(item, i) in group.items" :key="`item${i}`">{{ item }}</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="featured" id="featured">
        <h1>{{ summary.featured.name }}</h1>
        <div class="projects">
          <div v-for="project in projectList" :key="`pj-${project.id}`">
            <router-link :to="`/project/${project.id}`">
              <h2>{{ project.name }}</h2>
              <span>
                <template v-if="project.type === 'implementation'">
                  <img :src="`/icons/${projectTypeIcon.IMPLEMENTATION}-light.svg`" alt="" aria-label="A coding project">
                </template>
                <template v-else-if="project.type === 'research'">
                  <img :src="`/icons/${projectTypeIcon.RESEARCH}-light.svg`" alt="" aria-label="A research project" style="height:1.35rem;">
                </template>
                <template v-else-if="project.type === 'design'">
                  <img :src="`/icons/${projectTypeIcon.DESIGN}-light.svg`" alt="" aria-label="A design project">
                </template>
              </span>
              <p>{{ project.introduction }}</p>
            </router-link>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Navigation from '../components/Navigation';
import Header from '../components/Header';
import { mapState } from 'vuex'

export default {
  name: 'Home',
  title: 'Portfolio Home',
  components: {
    Navigation,
    Header
  },
  computed: {
    ...mapState([
      'projectTypeIcon',
      'showWhiteHeader',
      'summary',
      'projectList',
    ])
  },
  data() {
    return {
      roundAndRound: false,
      headerHeight: 55
    }
  },
  mounted() {
    this.headerHeight = document.getElementsByClassName('transparent-bar')[0].clientHeight;
    document.addEventListener('scroll', this.scroller);
    this.goAround();
  },
  destroyed() {
    document.removeEventListener('scroll', this.scroller);
  },
  methods: {
    goAround() {
      setTimeout(() => {
        this.roundAndRound = false;
      }, 500);
      this.roundAndRound = true;
    },
    scroller() {
      this.$store.dispatch('showWhiteNav', window.scrollY > 375);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../assets/css/variables';

.intro {
  @include bottom-diagonals(var(--diagonal-1x));
  padding-bottom: var(--space-2x);
  .container {
    background: rgba(52, 27, 43, 0.5);
    color: white;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    padding: var(--space-2x) var(--space-1x);
    a.box {
      border: 1px solid white;
      border-radius: 2px;
      background: transparent;
      color: white;
      @extend %font-regular-semi-bold;
      font-size: 1rem;
      line-height: 1.5rem;
      letter-spacing: 1px;
      padding: 1rem 3rem;
      text-align: center;
      text-decoration: none;
      text-transform: uppercase;
      width: 100%;
      &:hover, &:focus {
        background: rgba(255,255,255,.07);
      }
    }
    br {
      display: none;
    }
  }
  h1 {
    color: white;
    font-size: 3.25rem;
    line-height: calc(3.25rem * 1.15);
    //fonts-size: 10.5vw;
    padding: 2rem 0;
  }
  p {
    color: white;
    padding: 0.5rem 0 2rem;
    &.role {
      @extend %font-emphasis;
      font-size: 1.95rem;
      line-height: calc(1.95rem * 1.5);
      font-variant: small-caps;
      letter-spacing: 1px;
    }
  }
  h1,
  p {
    text-align: center;
  }
} // ~intro

main {
  @include four-diagonals(var(--diagonal-1x));
  padding: var(--space-2x) 0 var(--space-2x);
  h1 {
    font-size: 2.5rem;
    line-height: calc(2.5rem * 1.25);
  }
  h2 {
    font-size: 1.95rem;
    line-height: calc(1.95rem * 1.15);
    padding: 0.5rem 0 1rem;
  }
  .skillset,
  .featured {
    padding: var(--space-2x) var(--space-1x);
    text-align: center;
  }
  .skillset {
    background: $accent;
    margin: 0 var(--space-sideburn);
    h1,
    h2 {
      color: white;
    }
    .segments {
      display: grid;
      > div {
        padding: 1rem 0 1.5rem 0;
        &:last-child {
          padding-bottom: 0;
        }
      }
      img {
        @extend %color-white;
        opacity: 0.5;
        height: 4rem;
        margin: 2rem 0;
      }
      li {
        color: rgba(255, 255, 255, 0.85);
        display: inline-block;
        @extend %font-size-standard;
        &::after {
          content: ', ';
          white-space: pre;
        }
        &:last-child::after {
          content: '';
        }
      }
    }
  }

  .featured {
    background: white;
    color: $theme;
    margin: var(--space-1x) var(--space-sideburn) 0;
    .projects {
      display: grid;
      > div {
        border: 1px solid lighten($theme, 25%);
        margin: 1.5rem 0;
        padding: 1.5rem;
        position: relative;
        &:hover, &:focus {
          background-color: $hover;
          span {
            background-color: transparent;
            transform: scale(0.6);
            transition: all 0.5s ease-in-out;
          }
        }
        a:link,
        a:visited,
        a:hover,
        a:focus {
          color: $theme;
          text-decoration: none;
        }
        span {
          background-color: white;
          border-radius: 50%;
          padding: 1rem 0;
          position: absolute;
          top: -11px;
          left: -7px;
          height: 3.5rem;
          width: 3.5rem;
          transition: all .5s ease-in-out;
          img {
            @extend %color-raisin;
            height: 1.5rem;
          }
        }
      }
    }
  }
} // ~main

@media screen and (min-width: $md) {
  .intro .container {
    margin: 0 var(--space-sideburn);
    a.box {
      width: auto;
    }
  }
}
@media screen and (min-width: $lg) {
  .intro .container {
    br {
      display: block !important;
    }
  }
  main {
    .skillset {
      .segments {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }
    .featured {
      .projects {
        grid-template-columns: 1fr 1fr;
        grid-gap: 1rem;
      }
    }
  }
}
</style>
